import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Modal, 
  TextField, 
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../../components/buttons/Button';
import useCurrencyService from '../../../../../../components/hooks/useCurrencyService';

import { 
	CreatePaymentServiceBIZcoreCC, 
	GetPaymentCountryCurrencyXrefBIZcoreCC, 
  UpdatePaymentServiceBIZcoreCC
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import global from "../../../../../../global.scss";

const paymentTypeList = [
  { label: "Priority", value: "PRIORITY" },
  { label: "Regular", value: "REGULAR" },
]

const Data = {
  CountryCurrencyId: "",
  PaymentType: "",
	CutOffTime: "",
  MinLimit: "",
  MaxLimit: "",
  DeliveryTimeFrame: ""
};

const PaymentServicesModal = ({
  open, 
  setOpen, 
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
	const state = useSelector(state => state["business.core.cc"]);
  const { handleEditAmount, handleCalculateAmount } = useCurrencyService();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(1);

  const [paymentCountryCurrencyXrefList, setPaymentCountryCurrencyXrefList] = useState([]);

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(1);
  };

  useEffect(() => {
    try {
			if(open){
				dispatch(GetPaymentCountryCurrencyXrefBIZcoreCC(1, 1000, "", setLoading));
				
        if(update.defaultMode === 1){
          // mode = 1 - create payment service
          setMode(1);
        }else if(update.defaultMode === 2){
          // mode = 2 - edit payment service
          setMode(2);
          setFormData({
            ...formData,
            CountryCurrencyId: update.ref_pmt_country_currency_id?.id || "",
            PaymentType: update.payment_type || "",
            CutOffTime: update.cutoff_time || "",
            // min/max are stored as minor units — decode with the row's own
            // currency so 250000 shows as 2,500.00 rather than 250000
            MinLimit: handleEditAmount(
              update.ref_pmt_country_currency_id?.ref_payment_currency_id?.ref_currency_id?.code,
              update.min_limit || 0),
            MaxLimit: handleEditAmount(
              update.ref_pmt_country_currency_id?.ref_payment_currency_id?.ref_currency_id?.code,
              update.max_limit || 0),
            DeliveryTimeFrame: update.delivery_time_frame || ""
          });
        }else{
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

	useEffect(() => {
		try {
			formatPaymentCountryCurrencyXrefList(state.paymentCountryCurrencyXrefList.records || []);
		} catch (err) {}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state]);

  // The xref row carries no name/code of its own — both live two levels down.
  const xrefLabel = (item) => {
    const country = item?.ref_payment_country_id?.ref_country_id;
    const currency = item?.ref_payment_currency_id?.ref_currency_id;
    return `${country?.name || "?"} (${country?.code || "?"})  -  ${currency?.name || "?"} (${currency?.code || "?"})`;
  };

  // Sorted by that label — the raw API order is insertion order.
  const formatPaymentCountryCurrencyXrefList = (list) => {
    setPaymentCountryCurrencyXrefList([...list].sort((a, b) =>
      xrefLabel(a).localeCompare(xrefLabel(b))));
  };

  const selectedXref = paymentCountryCurrencyXrefList.find(
    (item) => item.id === formData.CountryCurrencyId);
  const selectedCurrencyCode =
    selectedXref?.ref_payment_currency_id?.ref_currency_id?.code || "";

  // --------------- handle change ----------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if(mode === 1){
      // create payment service
      let obj = {
        ref_pmt_country_currency_id: formData.CountryCurrencyId || "",
        payment_type: formData.PaymentType || "",
        cutoff_time: new Date(),
        min_limit: handleCalculateAmount(selectedCurrencyCode, formData.MinLimit || 0),
        max_limit: handleCalculateAmount(selectedCurrencyCode, formData.MaxLimit || 0),
        delivery_time_frame: formData.DeliveryTimeFrame || ""
        // ['ref_pmt_country_currency_id', 'payment_type', 'cutoff_time', 'min_limit', 'max_limit', 'delivery_time_frame']
      };
      dispatch(CreatePaymentServiceBIZcoreCC(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        })
      });
    }else if(mode === 2){
      // update payment service
      let obj = {
			  ref_pmt_country_currency_id: formData.CountryCurrencyId || "",
        payment_type: formData.PaymentType || "",
        cutoff_time: formData.CutOffTime || "",
        min_limit: handleCalculateAmount(selectedCurrencyCode, formData.MinLimit || 0),
        max_limit: handleCalculateAmount(selectedCurrencyCode, formData.MaxLimit || 0),
        delivery_time_frame: formData.DeliveryTimeFrame || ""
      };
      dispatch(UpdatePaymentServiceBIZcoreCC(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        })
      });
    }else{
      toast.error("Invalid modal");
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"}
            sx={{ padding: "15px"}}>
            <Typography variant='h6' 
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>
						<Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
							<TextField 
								select
								label="Country / Currency"
								fullWidth
								name="CountryCurrencyId"
								value={formData.CountryCurrencyId}
								onChange={handleChange}
								>
								{
									paymentCountryCurrencyXrefList.map((item, index) => (
										<MenuItem
											key={index}
											value={item.id}>
											{xrefLabel(item)}
										</MenuItem>
									))
								}
							</TextField>
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
							<TextField 
								select
								label="Payment type"
								fullWidth
								name="PaymentType"
								value={formData.PaymentType}
								onChange={handleChange}
								>
								{
									paymentTypeList.map((item, index) => (
										<MenuItem
											key={index}
											value={item.value}>
											{item.label}
										</MenuItem>
									))
								}
							</TextField>
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label={selectedCurrencyCode ? `Min limit (${selectedCurrencyCode})` : "Min limit"}
                fullWidth
                name="MinLimit"
                value={formData.MinLimit}
                onChange={handleChange}
                helperText={"Amount in major units, e.g. 2500.00"}
                />
              <TextField 
                label={selectedCurrencyCode ? `Max limit (${selectedCurrencyCode})` : "Max limit"}
                fullWidth
                name="MaxLimit"
                value={formData.MaxLimit}
                onChange={handleChange}
                helperText={"Amount in major units, e.g. 2500.00"}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label="Delivery time frame (days)"
                type='number'
                fullWidth
                name="DeliveryTimeFrame"
                value={formData.DeliveryTimeFrame}
                onChange={handleChange}
                />
            </Box>
          </Box>
          <Divider
           sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text'
              onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton 
              variant='contained'
              loading={loading}
              disabled={!(formData.CountryCurrencyId !== "" && formData.PaymentType !== "" && 
                formData.MinLimit !== "" && formData.MaxLimit !== "" && formData.DeliveryTimeFrame !== "")}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default PaymentServicesModal;